import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { ArrowRight } from 'lucide-react';
import PageHero from '../../components/ui/page-hero';
import ServiceSection from '../../components/services/ServiceSection';
import ServiceFAQ from '../../components/services/ServiceFAQ';
import StickyCTA from '../../components/services/StickyCTA';
import servicesData from '../../content/services/services.json';
import '../../components/services/services.css';

const Services = () => {
  useEffect(() => {
    // Smooth scroll behavior for better UX
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{servicesData.meta.title}</title>
        <meta name="description" content={servicesData.meta.description} />
        <meta name="keywords" content={servicesData.meta.keywords} />
        <meta property="og:title" content={servicesData.meta.title} />
        <meta property="og:description" content={servicesData.meta.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://envael.com/services" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={servicesData.meta.title} />
        <meta name="twitter:description" content={servicesData.meta.description} />
        <link rel="canonical" href="https://envael.com/services" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Page Hero with Navigation */}
        <PageHero 
          pageName="Services" 
          subtitle={servicesData.hero.subtitle}
          size="large"
          fullHeight={true}
          enableSpline={true}
          ctaButtons={servicesData.hero.cta}
        />

        {/* Services Sections */}
        {servicesData.services.map((service, index) => (
          <div key={service.id}>
            <ServiceSection 
              service={service} 
              index={index}
            />
            
            {/* FAQ Section for each service */}
            <ServiceFAQ 
              faqs={service.faq} 
              serviceTitle={service.title}
            />
          </div>
        ))}

        {/* Final CTA Section */}
        <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-primary/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
                Join 50+ successful businesses that have transformed their operations with our premium digital services. 
                Let's discuss how we can help you achieve your goals.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold text-lg group">
                  Book a Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform inline" />
                </button>
                
                <button className="px-8 py-4 border-2 border-primary/20 text-primary rounded-lg hover:bg-primary/5 transition-colors font-semibold text-lg">
                  Download Our Portfolio
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">100+</div>
                  <div className="text-sm text-muted-foreground">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">50+</div>
                  <div className="text-sm text-muted-foreground">Happy Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">5+</div>
                  <div className="text-sm text-muted-foreground">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">99%</div>
                  <div className="text-sm text-muted-foreground">Client Satisfaction</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sticky CTA */}
        <StickyCTA 
          title={servicesData.stickyCta.title}
          buttons={servicesData.stickyCta.buttons}
        />
      </div>
    </>
  );
};

export default Services;